import DateInput from '../components/DateInput';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientProfileAPI } from '../services/api';
import { PageHeader, ActionBtn, Card, Modal } from '../components/Shared';
import PatientReferralLettersTab from '../components/PatientReferralLettersTab';
import PatientFitNotesTab from '../components/PatientFitNotesTab';
import PatientPrivatePrescriptionsTab from '../components/PatientPrivatePrescriptionsTab';
import PatientAsyncRequestsTab from '../components/PatientAsyncRequestsTab'; // @admin-patient-requests-v1
import toast from 'react-hot-toast';
import styles from './Pages.module.css';

export default function PatientProfilePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [commForm, setCommForm] = useState({ communicationType: 'call', subject: '', notes: '', communicationDate: '' });

  // Audit log state — separate from profile state because it's lazy-loaded
  // (only when the user opens the audit tab) and paginated independently.
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditDownloading, setAuditDownloading] = useState(false);
  const auditLimit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await patientProfileAPI.getFullProfile(patientId);
      setPatient(data.data);
      setEditForm({
        firstName: data.data.firstName || '',
        lastName: data.data.lastName || '',
        email: data.data.email || '',
        phone: data.data.phone || '',
        gender: data.data.gender || '',
        dateOfBirth: data.data.dateOfBirth ? data.data.dateOfBirth.split('T')[0] : '',
        status: data.data.status || 'active'
      });
    } catch (err) {
      toast.error('Αδυναμία φόρτωσης προφίλ');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patientId]);

  // Lazy load audit logs when the audit tab is opened or the page changes.
  // Lazy because audit logs grow forever and we don't want to fetch them
  // every time someone opens a patient profile.
  const loadAuditLogs = async (page) => {
    setAuditLoading(true);
    try {
      const { data } = await patientProfileAPI.getAuditLogs(patientId, { page, limit: auditLimit });
      setAuditLogs(data.data || []);
      setAuditPage(data.pagination?.page || page);
      setAuditTotalPages(data.pagination?.totalPages || 1);
      setAuditTotal(data.pagination?.total || 0);
    } catch (err) {
      toast.error('Αδυναμία φόρτωσης ιστορικού πρόσβασης');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit' && patientId) {
      loadAuditLogs(1);
    }
    // eslint-disable-next-line
  }, [activeTab, patientId]);

  const handleDownloadCsv = async () => {
    setAuditDownloading(true);
    try {
      const response = await patientProfileAPI.downloadAuditLogsCsv(patientId);
      // Build a download link from the blob and click it
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `audit-logs-${patient.firstName}-${patient.lastName}-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Λήψη CSV ολοκληρώθηκε');
    } catch (err) {
      toast.error('Σφάλμα λήψης CSV');
    } finally {
      setAuditDownloading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await patientProfileAPI.updatePatient(patientId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        gender: editForm.gender,
        dateOfBirth: editForm.dateOfBirth,
        status: editForm.status
      });
      if (editForm.email !== patient.email) {
        await patientProfileAPI.updateEmail(patientId, editForm.email);
      }
      toast.success('Τα στοιχεία ενημερώθηκαν');
      setShowEditModal(false);
      load();
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleAddCommunication = async () => {
    try {
      await patientProfileAPI.addCommunication(patientId, commForm);
      toast.success('Επικοινωνία καταχωρήθηκε');
      setShowCommModal(false);
      setCommForm({ communicationType: 'call', subject: '', notes: '', communicationDate: '' });
      load();
    } catch (err) {
      toast.error('Σφάλμα');
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const StatusBadge = ({ status }) => {
    const colors = { active: 'var(--success)', inactive: 'var(--danger)' };
    const labels = { active: 'Ενεργός', inactive: 'Ανενεργός' };
    return React.createElement('span', { style: { padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: (colors[status] || 'var(--muted)') + '20', color: colors[status] || 'var(--muted)' } }, labels[status] || status);
  };

  const GenderLabel = ({ gender }) => {
    const labels = { male: 'Άνδρας', female: 'Γυναίκα', other: 'Άλλο' };
    return labels[gender] || gender || '-';
  };

  // Map raw entity_type/action codes from the backend to a single human-readable
  // Greek label. Centralised here so the same translation is used in both the
  // table and any future filtering / CSV preview.
  const describeAccess = (entityType, action) => {
    if (entityType === 'consultation' && action === 'view') return 'Διαβούλευση (προβολή)';
    if (entityType === 'consultation' && action === 'video_join') return 'Σύνδεση σε βιντεοκλήση';
    if (entityType === 'patient_profile') return 'Προφίλ ασθενή (προβολή)';
    if (entityType === 'medical_passport') return 'Ιατρικό διαβατήριο (προβολή)';
    if (entityType === 'consultation_history') return 'Ιστορικό διαβουλεύσεων (λίστα)';
    if (entityType === 'data_export') return 'Εξαγωγή δεδομένων (GDPR)';
    return `${entityType || '-'} (${action || '-'})`;
  };

  const roleLabel = (role) => {
    const labels = { doctor: 'Ιατρός', admin: 'Διαχειριστής', patient: 'Ασθενής', system: 'Σύστημα' };
    return labels[role] || role || '-';
  };

  if (loading) return React.createElement('div', { className: styles.page }, React.createElement('div', { className: styles.loading }, 'Φόρτωση...'));
  if (!patient) return null;

  const tabs = [
    { key: 'overview', label: 'Επισκόπηση' },
    { key: 'consultations', label: 'Διαβουλεύσεις' },
    { key: 'sickleave', label: 'Αναρρωτικές Άδειες' },      // @admin-patient-requests-v1
    { key: 'renewals', label: 'Επαναλήψεις Συνταγών' },
    { key: 'payments', label: 'Πληρωμές' },
    { key: 'communications', label: 'Επικοινωνία' },
    { key: 'referrals', label: 'Παραπεμπτικά' },
    { key: 'fitnotes', label: 'Ιατρικές Βεβαιώσεις' },
    { key: 'prescriptions', label: 'Ιδιωτικές Συνταγές' },
    { key: 'audit', label: 'Ιστορικό Πρόσβασης' },
  ];

  return React.createElement('div', { className: styles.page },
    React.createElement(PageHeader, {
      title: patient.firstName + ' ' + patient.lastName,
      subtitle: 'Ασθενής',
      action: React.createElement(ActionBtn, { onClick: () => navigate('/patients') }, '← Πίσω στους Ασθενείς')
    }),

    React.createElement('div', { style: { background: 'white', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid var(--border)', display: 'flex', gap: 24, alignItems: 'center' } },
      React.createElement('div', { style: { width: 80, height: 80, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 28, fontWeight: 700 } },
        (patient.firstName?.[0] || '') + (patient.lastName?.[0] || '')
      ),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 } }, patient.firstName + ' ' + patient.lastName),
        React.createElement('div', { style: { fontSize: 14, color: 'var(--muted)', marginBottom: 8 } }, patient.email),
        React.createElement('div', { style: { display: 'flex', gap: 12, alignItems: 'center' } },
          React.createElement(StatusBadge, { status: patient.status }),
          React.createElement(ActionBtn, { onClick: () => setShowEditModal(true) }, 'Επεξεργασία')
        )
      ),
      React.createElement('div', { style: { textAlign: 'right' } },
        React.createElement('div', { style: { fontSize: 12, color: 'var(--muted)' } }, 'Διαβουλεύσεις'),
        React.createElement('div', { style: { fontSize: 24, fontWeight: 700, color: 'var(--navy)' } }, patient.consultations?.length || 0)
      )
    ),

    React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid var(--border)', paddingBottom: 12 } },
      tabs.map(t => React.createElement('button', {
        key: t.key,
        onClick: () => setActiveTab(t.key),
        style: { padding: '10px 16px', border: 'none', background: activeTab === t.key ? 'var(--crimson)' : 'transparent', color: activeTab === t.key ? 'white' : 'var(--slate)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }
      }, t.label))
    ),

    activeTab === 'overview' && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 } },
      React.createElement(Card, { title: 'Προσωπικά Στοιχεία' },
        React.createElement('div', { style: { padding: 16 } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Όνομα'), React.createElement('strong', null, patient.firstName)),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Επώνυμο'), React.createElement('strong', null, patient.lastName)),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Email'), React.createElement('strong', null, patient.email)),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Τηλέφωνο'), React.createElement('strong', null, patient.phone || '-')),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Φύλο'), React.createElement('strong', null, React.createElement(GenderLabel, { gender: patient.gender }))),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Ημ. Γέννησης'), React.createElement('strong', null, patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString('el-GR') : '-')),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Ηλικία'), React.createElement('strong', null, calculateAge(patient.dateOfBirth)))
        )
      ),
      React.createElement(Card, { title: 'Στατιστικά' },
        React.createElement('div', { style: { padding: 16 } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Συνολικές Διαβουλεύσεις'), React.createElement('strong', null, patient.consultations?.length || 0)),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Συνολικές Πληρωμές'), React.createElement('strong', null, patient.payments?.length || 0)),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Σύνολο Δαπανών'), React.createElement('strong', null, '€' + (patient.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2))),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '12px 0' } }, React.createElement('span', { style: { color: 'var(--muted)' } }, 'Εγγραφή'), React.createElement('strong', null, patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('el-GR') : '-'))
        )
      )
    ),

    activeTab === 'consultations' && React.createElement(Card, { title: 'Ιστορικό Διαβουλεύσεων' },
      React.createElement('div', { style: { overflowX: 'auto' } },
        React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 13 } },
          React.createElement('thead', null,
            React.createElement('tr', { style: { borderBottom: '2px solid var(--border)' } },
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ημερομηνία'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ιατρός'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ειδικότητα'),
              React.createElement('th', { style: { textAlign: 'center', padding: 12 } }, 'Κατάσταση'),
              React.createElement('th', { style: { textAlign: 'center', padding: 12 } }, 'Βαθμολογία')
            )
          ),
          React.createElement('tbody', null,
            (patient.consultations || []).length === 0
              ? React.createElement('tr', null, React.createElement('td', { colSpan: 5, style: { textAlign: 'center', padding: 30, color: 'var(--muted)' } }, 'Δεν υπάρχουν διαβουλεύσεις'))
              : (patient.consultations || []).map((c, i) => React.createElement('tr', { key: i, onClick: () => navigate('/consultations/' + c.id), style: { borderBottom: '1px solid var(--border)', cursor: 'pointer' }, onMouseOver: (e) => e.currentTarget.style.background = 'var(--bg)', onMouseOut: (e) => e.currentTarget.style.background = 'transparent' },
                  React.createElement('td', { style: { padding: 12 } }, new Date(c.date).toLocaleDateString('el-GR') + ' ' + (c.startTime?.slice(0, 5) || '')),
                  React.createElement('td', { style: { padding: 12 } }, 'Δρ. ' + c.doctorName),
                  React.createElement('td', { style: { padding: 12 } }, c.specialty || '-'),
                  React.createElement('td', { style: { textAlign: 'center', padding: 12 } }, React.createElement('span', { style: { padding: '4px 8px', borderRadius: 8, fontSize: 11, background: c.status === 'completed' ? 'var(--success)20' : 'var(--bg)', color: c.status === 'completed' ? 'var(--success)' : 'var(--muted)' } }, c.status)),
                  React.createElement('td', { style: { textAlign: 'center', padding: 12 } }, c.rating ? React.createElement('span', { style: { color: 'var(--warning)' } }, '★ ' + c.rating) : '-')
                ))
          )
        )
      )
    ),

    activeTab === 'payments' && React.createElement(Card, { title: 'Ιστορικό Πληρωμών' },
      React.createElement('div', { style: { overflowX: 'auto' } },
        React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 13 } },
          React.createElement('thead', null,
            React.createElement('tr', { style: { borderBottom: '2px solid var(--border)' } },
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ημερομηνία'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ιατρός'),
              React.createElement('th', { style: { textAlign: 'right', padding: 12 } }, 'Ποσό'),
              React.createElement('th', { style: { textAlign: 'center', padding: 12 } }, 'Κατάσταση'),
              React.createElement('th', { style: { textAlign: 'right', padding: 12 } }, 'Επιστροφή')
            )
          ),
          React.createElement('tbody', null,
            (patient.payments || []).length === 0
              ? React.createElement('tr', null, React.createElement('td', { colSpan: 5, style: { textAlign: 'center', padding: 30, color: 'var(--muted)' } }, 'Δεν υπάρχουν πληρωμές'))
              : (patient.payments || []).map((p, i) => React.createElement('tr', { key: i, style: { borderBottom: '1px solid var(--border)' } },
                  React.createElement('td', { style: { padding: 12 } }, new Date(p.date).toLocaleDateString('el-GR')),
                  React.createElement('td', { style: { padding: 12 } }, 'Δρ. ' + p.doctorName),
                  React.createElement('td', { style: { textAlign: 'right', padding: 12, fontWeight: 600 } }, '€' + Number(p.amount || 0).toFixed(2)),
                  React.createElement('td', { style: { textAlign: 'center', padding: 12 } }, React.createElement('span', { style: { padding: '4px 8px', borderRadius: 8, fontSize: 11, background: p.status === 'completed' ? 'var(--success)20' : p.status === 'refunded' ? 'var(--warning)20' : 'var(--bg)', color: p.status === 'completed' ? 'var(--success)' : p.status === 'refunded' ? 'var(--warning)' : 'var(--muted)' } }, p.status)),
                  React.createElement('td', { style: { textAlign: 'right', padding: 12 } }, p.refundAmount ? '€' + Number(p.refundAmount).toFixed(2) : '-')
                ))
          )
        )
      )
    ),

    activeTab === 'communications' && React.createElement(Card, { title: 'Ημερολόγιο Επικοινωνίας', action: React.createElement(ActionBtn, { variant: 'primary', onClick: () => setShowCommModal(true) }, '+ Νέα Καταχώρηση') },
      React.createElement('div', { style: { padding: 16 } },
        (patient.communications || []).length === 0
          ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: 'var(--muted)' } }, 'Δεν υπάρχουν καταχωρήσεις')
          : (patient.communications || []).map((c, i) => React.createElement('div', { key: i, style: { padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 } },
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 } },
                React.createElement('span', { style: { padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--bg)', color: 'var(--navy)' } },
                  c.communication_type === 'call' ? 'Κλήση' : c.communication_type === 'email' ? 'Email' : c.communication_type === 'meeting' ? 'Συνάντηση' : 'Άλλο'
                ),
                React.createElement('span', { style: { fontSize: 12, color: 'var(--muted)' } }, new Date(c.communication_date).toLocaleDateString('el-GR'))
              ),
              c.subject && React.createElement('div', { style: { fontWeight: 600, marginBottom: 4 } }, c.subject),
              React.createElement('p', { style: { fontSize: 13, color: 'var(--slate)' } }, c.notes)
            ))
      )
    ),

    activeTab === 'audit' && React.createElement(Card, {
      title: `Ιστορικό Πρόσβασης (${auditTotal} καταχωρήσεις)`,
      action: React.createElement(ActionBtn, {
        variant: 'primary',
        onClick: handleDownloadCsv,
        disabled: auditDownloading || auditTotal === 0
      }, auditDownloading ? 'Λήψη...' : '⬇ Λήψη CSV')
    },
      React.createElement('div', { style: { overflowX: 'auto' } },
        React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 13 } },
          React.createElement('thead', null,
            React.createElement('tr', { style: { borderBottom: '2px solid var(--border)' } },
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ημερομηνία & Ώρα'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Όνομα'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ρόλος'),
              React.createElement('th', { style: { textAlign: 'left', padding: 12 } }, 'Ενέργεια')
            )
          ),
          React.createElement('tbody', null,
            auditLoading
              ? React.createElement('tr', null, React.createElement('td', { colSpan: 4, style: { textAlign: 'center', padding: 30, color: 'var(--muted)' } }, 'Φόρτωση...'))
              : auditLogs.length === 0
                ? React.createElement('tr', null, React.createElement('td', { colSpan: 4, style: { textAlign: 'center', padding: 30, color: 'var(--muted)' } }, 'Δεν υπάρχουν καταχωρήσεις πρόσβασης'))
                : auditLogs.map((log, i) => React.createElement('tr', { key: log.id || i, style: { borderBottom: '1px solid var(--border)' } },
                    React.createElement('td', { style: { padding: 12, whiteSpace: 'nowrap' } },
                      new Date(log.accessed_at).toLocaleString('el-GR', { dateStyle: 'short', timeStyle: 'medium' })
                    ),
                    React.createElement('td', { style: { padding: 12 } }, log.accessor_name || '-'),
                    React.createElement('td', { style: { padding: 12 } }, roleLabel(log.accessed_by_role)),
                    React.createElement('td', { style: { padding: 12 } }, describeAccess(log.entity_type, log.action))
                  ))
          )
        )
      ),
      // Pagination footer (only render if more than one page)
      auditTotalPages > 1 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderTop: '1px solid var(--border)' } },
        React.createElement('span', { style: { fontSize: 12, color: 'var(--muted)' } },
          `Σελίδα ${auditPage} από ${auditTotalPages}`
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(ActionBtn, {
            onClick: () => loadAuditLogs(auditPage - 1),
            disabled: auditPage <= 1 || auditLoading
          }, '← Προηγούμενη'),
          React.createElement(ActionBtn, {
            onClick: () => loadAuditLogs(auditPage + 1),
            disabled: auditPage >= auditTotalPages || auditLoading
          }, 'Επόμενη →')
        )
      )
    ),

    activeTab === 'referrals' && React.createElement(PatientReferralLettersTab, { patientId }),
    activeTab === 'fitnotes' && React.createElement(PatientFitNotesTab, { patientId }),
    activeTab === 'sickleave' && React.createElement(PatientAsyncRequestsTab, { patientId, kind: 'sick_leave' }),
    activeTab === 'renewals' && React.createElement(PatientAsyncRequestsTab, { patientId, kind: 'renewal' }),
    activeTab === 'prescriptions' && React.createElement(PatientPrivatePrescriptionsTab, { patientId }),
    React.createElement(Modal, { open: showEditModal, onClose: () => setShowEditModal(false), title: 'Επεξεργασία Ασθενή', size: 'md' },
      React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } },
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Όνομα'),
            React.createElement('input', { value: editForm.firstName, onChange: e => setEditForm({ ...editForm, firstName: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } })
          ),
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Επώνυμο'),
            React.createElement('input', { value: editForm.lastName, onChange: e => setEditForm({ ...editForm, lastName: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } })
          )
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Email'),
          React.createElement('input', { value: editForm.email, onChange: e => setEditForm({ ...editForm, email: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } })
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Τηλέφωνο'),
          React.createElement('input', { value: editForm.phone, onChange: e => setEditForm({ ...editForm, phone: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }, placeholder: '+30...' })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 } },
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Φύλο'),
            React.createElement('select', { value: editForm.gender, onChange: e => setEditForm({ ...editForm, gender: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } },
              React.createElement('option', { value: '' }, 'Επιλέξτε...'),
              React.createElement('option', { value: 'male' }, 'Άνδρας'),
              React.createElement('option', { value: 'female' }, 'Γυναίκα'),
              React.createElement('option', { value: 'other' }, 'Άλλο')
            )
          ),
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Ημ. Γέννησης'),
            React.createElement(DateInput, { value: editForm.dateOfBirth, onChange: e => setEditForm({ ...editForm, dateOfBirth: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } })
          )
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Κατάσταση'),
          React.createElement('select', { value: editForm.status, onChange: e => setEditForm({ ...editForm, status: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } },
            React.createElement('option', { value: 'active' }, 'Ενεργός'),
            React.createElement('option', { value: 'inactive' }, 'Ανενεργός')
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 } },
          React.createElement(ActionBtn, { onClick: () => setShowEditModal(false) }, 'Ακύρωση'),
          React.createElement(ActionBtn, { variant: 'primary', onClick: handleSaveEdit }, 'Αποθήκευση')
        )
      )
    ),

    React.createElement(Modal, { open: showCommModal, onClose: () => setShowCommModal(false), title: 'Νέα Επικοινωνία', size: 'md' },
      React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Τύπος'),
          React.createElement('select', { value: commForm.communicationType, onChange: e => setCommForm({ ...commForm, communicationType: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } },
            React.createElement('option', { value: 'call' }, 'Τηλεφωνική Κλήση'),
            React.createElement('option', { value: 'email' }, 'Email'),
            React.createElement('option', { value: 'meeting' }, 'Συνάντηση'),
            React.createElement('option', { value: 'other' }, 'Άλλο')
          )
        ),
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Ημερομηνία'),
          React.createElement(DateInput, { value: commForm.communicationDate, onChange: e => setCommForm({ ...commForm, communicationDate: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' } })
        ),
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Θέμα'),
          React.createElement('input', { value: commForm.subject, onChange: e => setCommForm({ ...commForm, subject: e.target.value }), style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }, placeholder: 'π.χ. Επιβεβαίωση ραντεβού' })
        ),
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { display: 'block', marginBottom: 8, fontWeight: 600 } }, 'Σημειώσεις'),
          React.createElement('textarea', { value: commForm.notes, onChange: e => setCommForm({ ...commForm, notes: e.target.value }), rows: 4, style: { width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }, placeholder: 'Λεπτομέρειες επικοινωνίας...' })
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          React.createElement(ActionBtn, { onClick: () => setShowCommModal(false) }, 'Ακύρωση'),
          React.createElement(ActionBtn, { variant: 'primary', onClick: handleAddCommunication, disabled: !commForm.notes }, 'Καταχώρηση')
        )
      )
    )
  );
}
